import { HttpErrorResponse } from '@angular/common/http';
import type { ErrorHandler } from '@angular/core';
import { Injectable } from '@angular/core';

import { getOriginalConsoleError } from './early-error-buffer';
import { captureException } from './sentry-lite';

/**
 * Replacement for Sentry's `createErrorHandler` that routes through `sentry-lite` instead of the
 * SDK, so `@sentry/angular` stays off the critical path. Error extraction mirrors the SDK's default
 * extractor so reported events keep the same shape.
 */
@Injectable()
export class LazySentryErrorHandler implements ErrorHandler {
    public handleError(error: unknown): void {
        const extractedError = extractError(error) ?? 'Handled unknown error';

        captureException(extractedError, {
            mechanism: { type: 'auto.function.angular.error_handler', handled: false },
        });

        // Bypasses the patched console so the log does not land back in the early buffer.
        getOriginalConsoleError()(extractedError);
    }
}

function extractError(errorCandidate: unknown): string | Error | null {
    const error = unwrapNgError(errorCandidate);

    if (error instanceof HttpErrorResponse) {
        return extractHttpError(error);
    }

    if (typeof error === 'string' || isErrorLike(error)) {
        return error;
    }

    return null;
}

function unwrapNgError(error: unknown): unknown {
    return (error as { ngOriginalError?: Error })?.ngOriginalError ?? error;
}

function extractHttpError(error: HttpErrorResponse): string | Error {
    if (isErrorLike(error.error)) {
        return error.error;
    }

    if (typeof ErrorEvent !== 'undefined' && error.error instanceof ErrorEvent && error.error.message) {
        return error.error.message;
    }

    if (typeof error.error === 'string') {
        return `Server returned code ${error.status} with body "${error.error}"`;
    }

    return error.message;
}

function isErrorLike(value: unknown): value is Error {
    if (value instanceof Error) {
        return true;
    }

    if (value === null || typeof value !== 'object') {
        return false;
    }

    const candidate = value as { name?: unknown; message?: unknown; stack?: unknown };

    return (
        typeof candidate.name === 'string' &&
        typeof candidate.message === 'string' &&
        (candidate.stack === undefined || typeof candidate.stack === 'string')
    );
}
